import { t } from '../../../i18n';

interface ValidationNode {
  [key: string]: string[] | ValidationNode;
}

const FIELD_LABELS: Record<string, string> = {
  title: t('dash.admin.checklists.title_label'),
  status: t('dash.admin.checklists.status_label'),
  description: t('dash.admin.checklists.description_label'),
  legal_entity_ids: t('dash.admin.checklists.legal_entity_label'),
  position_ids: t('dash.admin.checklists.linked_positions_label'),
  company_ids: t('dash.admin.checklists.linked_units_label'),
  yes_no_question: t('dash.admin.checklists.question_label'),
  attachment: t('dash.admin.checklists.attachment_label'),
};

const formatKey = (key: string): string =>
  FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase());

// Flattens the nested `validations` map into "<Field> <message>" strings
const collectMessages = (node: ValidationNode, prefix = ''): string[] =>
  Object.entries(node).flatMap(([key, value]) => {
    if (key === 'tasks' && !Array.isArray(value)) {
      const taskPrefix = `${t('dash.admin.checklists.task_error_prefix')}: `;

      return Object.values(value).flatMap(taskErrors => collectMessages(taskErrors as ValidationNode, taskPrefix));
    }

    return Array.isArray(value) ? value.map(message => `${prefix}${formatKey(key)} ${message}`) : [];
  });

export interface ChecklistErrorResult {
  status?: number;
  messages?: string[];
  data?: { validations?: ValidationNode };
}

export const isSuccessStatus = (status?: number): boolean =>
  typeof status === 'number' && status >= 200 && status < 300;

export const buildChecklistErrorMessage = (result?: ChecklistErrorResult): string => {
  const validations = result?.data?.validations;
  const fieldMessages = validations ? Array.from(new Set(collectMessages(validations))) : [];

  if (fieldMessages.length > 0) {
    return t('dash.admin.checklists.validation_error_message', { errors: fieldMessages.join('; ') });
  }

  return result?.messages?.length ? result.messages.join(' ') : t('dash.common.notification.something_went_wrong');
};
